import { createContext } from "react";
import { useModalsController } from "./controllers/useModalsController";
import { useRerunVerificationController } from "./controllers/useRerunVerificationController";
import { useIncomeVerificationsSelectionController } from "./controllers/useVerificationSelectionController";
import { useReportDataController } from "./controllers/useReportDataController";
import { useApplicationRentActivityLogsController } from "./controllers/useApplicationRentActivityLogsController";
import { useReportUIStateController } from "./controllers/useReportUIStateController";
import {
  CombinedIncomeContextValue,
  CombinedIncomeProviderProps
} from "./types";

export const CombinedIncomeContext =
  createContext<CombinedIncomeContextValue | null>(null);

export const CombinedIncomeProvider = ({
  applicationId,
  applicantId,
  applicant,
  applicationStatus,
  activeVerification,
  applicationMeta,
  hasCreditReport,
  verificationId,
  children
}: CombinedIncomeProviderProps) => {
  const composedIncomeVerificationsData =
    useIncomeVerificationsSelectionController({
      applicant,
      initialVerificationId: verificationId
    });

  const reportModals = useModalsController();

  const {
    verificationData,
    verificationReportData,
    settings,
    incomes,
    filteredBankStatements,
    incomeSourceProvidersMap,
    incomeSourceTrustScoresMap,
    pendingReverifyRequests,
    completedReverifyRequests,
    failedReverifyRequests,
    verificationStatus,
    isLoading,
    isError,
    refetch
  } = useReportDataController({
    applicationId,
    applicantId,
    currentlySelectedVerificationId:
      composedIncomeVerificationsData.currentlySelectedVerificationId
  });

  const applicationRentActivityLogsMeta =
    useApplicationRentActivityLogsController({
      applicationId: applicant.application_id
    });

  const uiState = useReportUIStateController();

  const { rerun, isRerunning, cancelRerun, isCancelingRerun } =
    useRerunVerificationController({
      applicationId,
      activeVerificationId:
        composedIncomeVerificationsData.activeVerificationId,
      applicantId,
      refetch,
      setShowCancelRerunModal: reportModals.setShowCancelRerunModal,
      setCurrentlySelectedVerificationId:
        composedIncomeVerificationsData.setCurrentlySelectedVerificationId
    });

  return (
    <CombinedIncomeContext.Provider
      value={{
        applicationId,
        applicantId,
        applicant,
        applicationStatus,
        activeVerification,
        applicationMeta,
        hasCreditReport,
        ...composedIncomeVerificationsData,
        verificationData,
        verificationReportData,
        settings,
        incomes,
        bankStatements: filteredBankStatements,
        incomeSourceProvidersMap,
        incomeSourceTrustScoresMap,
        pendingReverifyRequests,
        completedReverifyRequests,
        failedReverifyRequests,
        verificationStatus,
        isLoading,
        isError,
        refetch,
        applicationRentActivityLogsMeta,
        reportModals,
        rerun,
        isRerunning,
        cancelRerun,
        isCancelingRerun,
        ...uiState
      }}
    >
      {children}
    </CombinedIncomeContext.Provider>
  );
};
